<template>
  <div role="region" aria-labelledby="memory-game-heading" tabindex="0">

    <h3 id="memory-game-heading" class="sr-only">{{ $t('Accessibility.MemoryGame.Title') }}</h3>

    <!-- Game instructions for screen readers -->
    <div class="sr-only" aria-live="polite" id="memory-instructions">
      {{ gameInstructions }}
    </div>

    <!-- Current game state announcement -->
    <div class="sr-only" aria-live="polite" id="memory-status">
      {{ gameStatus }}
    </div>

    <!-- Heading Image -->
    <div v-if="data && data.headingImageUrl" class="memory-game-heading-image" :style="{ height: imageMaxHeight }">
      <img :src="data.headingImageUrl" :alt="$t('Accessibility.MemoryGame.HeadingImage')" class="heading-image"
        :style="{ maxHeight: imageMaxHeight }" />
    </div>

    <!-- Intro Text -->
    <div v-if="data && data.introText" class="memory-game-intro-text text-center mt-3 mb-3"
      :style="introTextStyle()">
      {{ data.introText }}
    </div>

    <!-- Memory Game Grid -->
    <div class="memory-game-container" :class="{ 'game-completed': completed, 'game-disabled': showingCards }"
      role="grid" :aria-label="gridAriaLabel">

      <div class="memory-grid" :style="gridStyle()" ref="memoryGrid">

        <div v-for="(card, index) in cards" :key="index" class="memory-card" :class="{
          'flipped': card.isFlipped,
          'matched': card.isMatched,
          'disabled': showingCards || card.isMatched
        }" :style="cardStyle()" @click="onCardClick(index)" @keydown.enter="onCardClick(index)"
          @keydown.space.prevent="onCardClick(index)" :tabindex="(showingCards || card.isMatched) ? -1 : 0"
          role="button" :aria-label="getCardAriaLabel(card, index)" :aria-pressed="card.isFlipped">

          <!-- Card Front (Hidden Image) -->
          <div class="card-front">
            <img v-if="data && data.cardBackImageUrl" :src="data.cardBackImageUrl"
              :alt="$t('Accessibility.MemoryGame.HiddenCard')" class="card-image" />
            <div v-else class="card-placeholder card-hidden">
              ?
            </div>
          </div>

          <!-- Card Back (Revealed Image) -->
          <div class="card-back">
            <img v-if="card.imageUrl" :src="card.imageUrl"
              :alt="card.altText || $t('Accessibility.MemoryGame.RevealedCard')" class="card-image" />
            <div v-else class="card-placeholder">
              {{ index + 1 }}
            </div>
          </div>
        </div>
      </div>
    </div>


    <!-- Game Statistics for Screen Readers -->
    <div class="sr-only" aria-live="polite" v-if="gameStarted && !completed">
      {{ $t('Accessibility.MemoryGame.GameStats', { matches: matchedPairs, total: totalPairs }) }}
    </div>

    <!-- Keyboard instructions -->
    <div class="sr-only" v-if="!gameStarted">
      {{ $t('Accessibility.MemoryGame.KeyboardInstructions') }}
    </div>

    <!-- End Game Text -->
    <div v-if="completed && data && data.endGameText" class="memory-game-end-text text-center mt-3 mb-3"
      :style="endGameTextStyle()">
      {{ data.endGameText }}
    </div>

    <!-- Footer Image -->
    <div v-if="data && data.footerImageUrl" class="memory-game-footer-image" :style="{ height: imageMaxHeight }">
      <img :src="data.footerImageUrl" :alt="$t('Accessibility.MemoryGame.FooterImage')" class="footer-image"
        :style="{ maxHeight: imageMaxHeight }" />
    </div>

  </div>
</template>

<script>

import MemoryGame from './subcomponents/memorygame';

export default {
  props: {
    data: {},
    isDemo: false,
    inFrame: false,
    fontFamily: null,
    fontSize: null,
    viewportDimensions: {
      type: Object,
      default: () => ({ isDesktop: false })
    }
  },
  components: {
    //  MemoryGame logic handled in subcomponents
  },
  data() {
    return {
      completed: false,
      gameStarted: false,
      gameStartTime: null,
      gameEndTime: null,
      memoryGame: null,
      cards: [],
      flippedCards: [],
      matchedPairs: 0,
      totalPairs: 0,
      showingCards: false,
      gameStatus: '',
      canFlipCards: true
    };
  },
  computed: {
    gameInstructions() {
      return this.$t('Accessibility.MemoryGame.Instructions');
    },
    gridAriaLabel() {
      if (this.completed) {
        return this.$t('Accessibility.MemoryGame.Completed');
      } else if (!this.gameStarted) {
        return this.$t('Accessibility.MemoryGame.Ready');
      } else {
        return this.$t('Accessibility.MemoryGame.Playing', {
          matches: this.matchedPairs,
          total: this.totalPairs
        });
      }
    },
    imageScale() {
      // Apply 90% scale factor on desktop, 100% on mobile
      return this.viewportDimensions.isDesktop ? 0.90 : 1;
    },
    imageMaxHeight() {
      // Calculate max height based on scale factor (base mobile height is 160px)
      const baseHeight = 160;
      return this.viewportDimensions.isDesktop ?
        Math.round(baseHeight * this.imageScale) + 'px' :
        baseHeight + 'px';
    },
    maxCardSizes() {
      // Easily configurable max card sizes
      return {
        desktop: 160,  // Max card size on desktop
        mobile: 180    // Max card size on mobile
      };
    }
  },
  mounted() {
    this.initializeGame();
  },
  methods: {
    initializeGame() {
      if (!this.data) {
        return;
      }

      // Calculate pairs based on CardFaceImages count
      const availableImages = this.data.cardFaceImages || [];
      this.totalPairs = Math.max(1, availableImages.length);
      this.matchedPairs = 0;
      this.createCards();

      // Initialize the memory game logic
      this.memoryGame = new MemoryGame(
        this.data,
        this.cards,
        this.onCardMatch.bind(this),
        this.onGameComplete.bind(this),
        this.onCardsShowTimeout.bind(this)
      );

      // Game is ready to start on first card click
      this.canFlipCards = true;
    },

    createCards() {
      if (!this.data) {
        return;
      }

      const availableImages = this.data.cardFaceImages || [];
      const totalPairs = Math.max(1, availableImages.length);

      // Create pairs of cards
      let cardPairs = [];
      for (let i = 0; i < totalPairs; i++) {
        // Use available card face images or create placeholder
        let imageUrl, altText;
        if (availableImages.length > 0) {
          const item = availableImages[i % availableImages.length];
          imageUrl = item.imageUrl;
          altText = item.altText || `Card ${i + 1}`;

          // If backend didn't provide imageUrl, use CSS placeholder
          if (!imageUrl) {
            imageUrl = null; // Will trigger CSS placeholder
            altText = `Card ${i + 1}`;
          }
        } else {
          // Fallback for testing
          imageUrl = null; // Will trigger CSS placeholder
          altText = `Card ${i + 1}`;
        }

        cardPairs.push(
          { imageUrl, altText, pairId: i, isFlipped: false, isMatched: false },
          { imageUrl, altText, pairId: i, isFlipped: false, isMatched: false }
        );
      }

      // Shuffle the cards
      this.cards = this.shuffleArray(cardPairs);
    },

    shuffleArray(array) {
      const shuffled = [...array];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      return shuffled;
    },

    startGame() {
      if (!this.gameStarted) {
        this.gameStarted = true;
        this.gameStartTime = Date.now();
        this.gameStatus = this.$t('Accessibility.MemoryGame.Started');

        // Announce start to screen readers
        this.announceGameState(this.gameStatus);

        this.$emit('start');

        // Start the game immediately without revealing cards
        if (this.memoryGame) {
          this.memoryGame.onCardsHidden();
        }
      }
    },

    onCardClick(index) {
      if (!this.canFlipCards || this.showingCards || this.completed) {
        return;
      }

      // Start the game on first card click
      if (!this.gameStarted) {
        this.startGame();
      }

      const card = this.cards[index];

      if (card.isFlipped || card.isMatched) {
        return;
      }

      // Only allow flipping if less than 2 cards are currently flipped
      if (this.flippedCards.length >= 2) {
        return;
      }

      // Flip the card immediately
      card.isFlipped = true;
      this.flippedCards.push(index);

      // Check for match if two cards are flipped
      if (this.flippedCards.length === 2) {
        setTimeout(() => {
          this.canFlipCards = false;
          this.checkForMatch();
        }, this.data.revealTime);
      }
    },

    checkForMatch() {
      if (this.flippedCards.length !== 2) {
        return;
      }

      const [firstIndex, secondIndex] = this.flippedCards;
      const firstCard = this.cards[firstIndex];
      const secondCard = this.cards[secondIndex];

      // Check if cards match by comparing pairId
      if (firstCard.pairId === secondCard.pairId) {
        // Match found! Keep them flipped and mark as matched
        firstCard.isMatched = true;
        secondCard.isMatched = true;
        // Keep isFlipped true so they continue showing the revealed image
        firstCard.isFlipped = true;
        secondCard.isFlipped = true;
        this.matchedPairs++;

        this.gameStatus = this.$t('Accessibility.MemoryGame.Match', { matches: this.matchedPairs });
        this.announceGameState(this.gameStatus);

        // Check if game is complete
        if (this.matchedPairs === this.totalPairs) {
          setTimeout(() => {
            this.onGameComplete();
          }, 500);
        }
      } else {
        // No match - flip cards back
        firstCard.isFlipped = false;
        secondCard.isFlipped = false;
      }

      // Reset for next turn
      this.flippedCards = [];
      this.canFlipCards = true;
    },

    onCardMatch(cardIndices) {
      this.matchedPairs++;
      this.gameStatus = this.$t('Accessibility.MemoryGame.Match', { matches: this.matchedPairs });
      this.announceGameState(this.gameStatus);

      // Mark cards as matched
      cardIndices.forEach(index => {
        this.cards[index].isMatched = true;
      });

      // Clear flipped cards after match
      this.flippedCards = [];
    },

    onGameComplete() {
      this.completed = true;
      this.gameEndTime = Date.now();
      this.gameStatus = this.$t('Accessibility.MemoryGame.Completed');

      // Announce completion to screen readers
      this.announceGameState(this.gameStatus);

      // Calculate game duration in seconds
      const timeInSeconds = this.gameStartTime ? Math.round((this.gameEndTime - this.gameStartTime) / 1000) : 0;

      this.$emit('end', {
        score: timeInSeconds,
        level: timeInSeconds,
        timePlayed: timeInSeconds
      });
    },

    onCardsShowTimeout() {
      // Called when cards need to be flipped back after no match
      setTimeout(() => {
        this.cards.forEach(card => {
          if (!card.isMatched && card.isFlipped) {
            card.isFlipped = false;
          }
        });
        this.flippedCards = [];
      }, 750); // Show for 1 second before flipping back
    },


    announceGameState(message) {
      // Use the global screen reader announcement function if available
      if (this.$root.announceToScreenReader) {
        this.$root.announceToScreenReader(message);
      }
    },

    getCardAriaLabel(card, index) {
      const cols = this.getGridColumns();
      const position = `${Math.floor(index / cols) + 1}, ${(index % cols) + 1}`;
      if (card.isMatched) {
        return this.$t('Accessibility.MemoryGame.MatchedCard', { position });
      } else if (card.isFlipped) {
        return this.$t('Accessibility.MemoryGame.FlippedCard', { position });
      } else {
        return this.$t('Accessibility.MemoryGame.HiddenCardPosition', { position });
      }
    },

    getGridColumns() {
      const totalCards = this.cards.length;

      // Use configured columns or fall back to defaults (mobile: 2, desktop: 4)
      const cols = this.viewportDimensions.isDesktop
        ? (this.data && this.data.gridColumnsDesktop ? this.data.gridColumnsDesktop : 4)
        : (this.data && this.data.gridColumnsMobile ? this.data.gridColumnsMobile : 2);

      // Ensure at least 1 column and no more columns than cards
      return Math.max(1, Math.min(cols, totalCards));
    },

    gridStyle() {
      if (!this.data || !this.cards.length) return {};

      const cols = this.getGridColumns();
      const totalCards = this.cards.length;
      const rows = Math.ceil(totalCards / cols);

      return {
        gridTemplateColumns: `repeat(${cols}, 1fr)`,
        gridTemplateRows: `repeat(${rows}, 1fr)`,
        gap: '10px',
        maxWidth: '100%',
        margin: '0 auto'
      };
    },

    textStyle(color, size) {
      let style = {};
      if (color) style.color = color;
      if (size) style.fontSize = size + 'px';
      else if (this.fontSize) style.fontSize = this.fontSize + 'px';
      if (this.fontFamily) style.fontFamily = this.fontFamily;
      return style;
    },

    introTextStyle() {
      return this.textStyle(
        this.data && this.data.introTextColor,
        this.data && this.data.introTextFontSize
      );
    },

    endGameTextStyle() {
      return this.textStyle(
        this.data && this.data.endGameTextColor,
        this.data && this.data.endGameTextFontSize
      );
    },

    cardStyle() {
      if (!this.data) {
        return {
          width: '100px',
          height: '100px',
          fontSize: '16px',
          fontFamily: 'inherit'
        };
      }

      const cols = this.getGridColumns();
      const gap = 10 * (cols - 1); // Gap between cards
      const maxSizes = this.maxCardSizes;

      let cardWidth, cardHeight;

      if (this.viewportDimensions.isDesktop) {
        // Desktop: Use smaller cards with more margin
        const containerWidth = this.viewportDimensions.width || 768;
        const desktopMargin = 160; // Much larger margin for desktop (80px each side)
        const availableWidth = containerWidth - desktopMargin - gap;
        cardWidth = Math.floor(availableWidth / cols);
        // Limit max card size on desktop
        cardWidth = Math.min(cardWidth, maxSizes.desktop);
      } else {
        // Mobile: Use full width with small margins
        const screenWidth = window.innerWidth;
        const mobileMargin = 20; // Small margin (10px each side)
        const availableWidth = screenWidth - mobileMargin - gap;
        cardWidth = Math.floor(availableWidth / cols);
        // Limit max card size on mobile
        cardWidth = Math.min(cardWidth, maxSizes.mobile);
      }

      cardHeight = cardWidth; // Square cards

      return {
        width: `${cardWidth}px`,
        height: `${cardHeight}px`,
        fontSize: this.fontSize ? `${this.fontSize}px` : '16px',
        fontFamily: this.fontFamily || 'inherit'
      };
    },

  }
};

</script>

<style scoped>
/* Screen reader only content */
.sr-only {
  position: absolute !important;
  width: 1px !important;
  height: 1px !important;
  padding: 0 !important;
  margin: -1px !important;
  overflow: hidden !important;
  clip: rect(0, 0, 0, 0) !important;
  white-space: nowrap !important;
  border: 0 !important;
}

.top-image-container {
  text-align: center;
  margin-bottom: 20px;
}

.top-image {
  max-width: 100%;
  max-height: 120px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.memory-game-container {
  padding: 10px;
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  justify-content: center;
}

.memory-grid {
  display: grid;
  gap: 10px;
  max-width: 100%;
  margin: 0 auto;
}

.memory-card {
  position: relative;
  cursor: pointer;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  transform-style: preserve-3d;
  perspective: 1000px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  background: #fff;
  border-radius: v-bind('data && data.cardBorderRadius ? data.cardBorderRadius + "px" : "8px"');
}

/* Border overlay for normal cards */
.memory-card:not(.matched):not(.flipped)::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: v-bind('data && data.cardBorderSize ? data.cardBorderSize + "px" : "2px"') solid v-bind('data && data.cardBorderColor || "#ddd"');
  border-radius: v-bind('data && data.cardBorderRadius ? data.cardBorderRadius + "px" : "8px"');
  pointer-events: none;
  z-index: 10;
}

/* Border overlay for matched cards */
.memory-card.matched::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: v-bind('data && data.cardBorderSize ? data.cardBorderSize + "px" : "2px"') solid v-bind('data && data.cardMatchedBorderColor || "#28a745"');
  border-radius: v-bind('data && data.cardBorderRadius ? data.cardBorderRadius + "px" : "8px"');
  pointer-events: none;
  z-index: 10;
}

/* Border overlay for flipped cards */
.memory-card.flipped:not(.matched)::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: v-bind('data && data.cardBorderSize ? data.cardBorderSize + "px" : "2px"') solid #ddd;
  border-radius: v-bind('data && data.cardBorderRadius ? data.cardBorderRadius + "px" : "8px"');
  pointer-events: none;
  z-index: 10;
}

.memory-card:hover:not(.disabled) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.memory-card:focus:not(.disabled) {
  outline: 2px solid v-bind('data && data.cardFocusBorderColor || "#76b154"');
  outline-offset: 2px;
}


.memory-card.matched {
  opacity: 0.8;
  border-color: v-bind('data && data.cardMatchedBorderColor || "#28a745"');
  background-color: #f8f9fa;
  pointer-events: none;
  transform: scale(0.95);
}

.memory-card.matched.flipped {
  transform: scale(0.95);
}

.memory-card.matched .card-front {
  display: none;
}

.memory-card.matched .card-back {
  transform: rotateY(0deg);
}

.memory-card.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.card-front,
.card-back {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: v-bind('data && data.cardBorderRadius ? data.cardBorderRadius + "px" : "8px"');
  backface-visibility: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.card-back {
  transform: rotateY(180deg);
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.game-completed {
  opacity: 0.9;
}

.game-disabled {
  pointer-events: none;
}

/* Heading and Footer Images */
.memory-game-heading-image,
.memory-game-footer-image {
  text-align: center;
  margin: 20px 0;
  overflow: hidden;
  /* Prevent scaled images from overflowing */
}

.heading-image,
.footer-image {
  max-width: 100%;
  height: 100%;
  object-fit: contain;
  transition: transform 0.3s ease;
  transform-origin: center center;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .memory-game-container {
    padding: 10px;
  }

  .memory-grid {
    gap: 8px;
  }

  .memory-card {
    border-width: 1px;
  }

  .memory-game-heading-image,
  .memory-game-footer-image {
    margin: 15px 0;
  }
}

@media (max-width: 480px) {
  .memory-game-container {
    padding: 5px;
  }

  .memory-grid {
    gap: 5px;
  }

  .top-image {
    max-height: 80px;
  }

  .memory-game-heading-image,
  .memory-game-footer-image {
    margin: 10px 0;
  }

  .heading-image,
  .footer-image {
    max-height: 120px;
  }
}

/* Card placeholder styling */
.card-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2em;
  font-weight: bold;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 6px;
}

.card-hidden {
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  font-size: 3em;
}

/* Animation for card flip */
.memory-card {
  transition: transform 0.3s ease;
  transform-style: preserve-3d;
}

.memory-card.flipped {
  transform: rotateY(180deg);
}

.card-front,
.card-back {
  transition: none;
}

/* Pulse animation for matched cards */
@keyframes pulse {
  0% {
    transform: scale(0.95);
  }

  50% {
    transform: scale(1.05);
  }

  100% {
    transform: scale(0.95);
  }
}

.memory-card.matched {
  animation: pulse 0.6s ease-in-out;
}
</style>